/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useMemo } from 'react';
import { useDragLayer, useDrop } from 'react-dnd';
import tinycolor from 'tinycolor2';

import StatusCard from '@totem/components/ticketing/details/tasks/kanban/statusCard';
import TaskCard from '@totem/components/ticketing/details/tasks/kanban/taskCard';
import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketPermissions } from '@totem/components/ticketing/permissions';
import {
  Task,
  TASK_TYPE_GROUP,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  status: string;
  color: string;
};

const StatusColumn = ({ status, color }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const { tasks, taskWorkflow, onTaskStatusChange } = useContext(TaskContext);

  const statusTask = useMemo(() => {
    const filteredTasks = tasks.filter(
      (chk) => chk.status === status && chk.type !== TASK_TYPE_GROUP,
    );

    if (permissions.internal.hasPermissions(userProfile)) {
      return filteredTasks;
    }

    // IBD-623: Expose Task to External
    return filteredTasks.filter(
      (tsk) =>
        !tsk.internalOnly ||
        (tsk.type === TASK_TYPE_TASK &&
          ticketPermissions.taskStatusChanger.hasPermissions(
            userProfile,
            ticket,
            tsk,
            null,
          )),
    );
  }, [tasks]);

  const onDrop = (task: Task) => {
    const currentStatus = taskWorkflow.find((chk) => chk.code === task.status);
    if (isNotNull(currentStatus)) {
      if (currentStatus.options.includes(status)) {
        // Send Update Task Status
        onTaskStatusChange(status, task);
      }
    }
  };

  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: TASK_TYPE_TASK,
    drop: (item: Task) => {
      onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }), // Are we hovering this drop zone
      isDragging: !!monitor.getItemType(), // Is *any* drag happening
    }),
  });

  const coreStatuses = [
    'PENDING',
    'SCHEDULED',
    'IN PROGRESS',
    'REVIEW',
    'COMPLETED',
  ];

  const isDraggingTasks = useDragLayer((monitor) => {
    const isTaskDragging =
      monitor.isDragging() && monitor.getItemType() === TASK_TYPE_TASK;
    let isStatusDroppable = false;
    if (isTaskDragging) {
      const taskItem = monitor.getItem<Task>();

      const currentStatus = taskWorkflow.find(
        (chk) => chk.code === taskItem.status,
      );
      if (isNotNull(currentStatus)) {
        if (currentStatus.options.includes(status)) {
          isStatusDroppable = true;
        }
      }
    }

    return isTaskDragging && isStatusDroppable;
  });

  const background = isDraggingTasks
    ? tinycolor.mix(color, '#FFFFFF', 80).toHexString()
    : null;

  return (
    <>
      {isNotNull(statusTask) &&
        (statusTask.length > 0 ||
          coreStatuses.includes(status) ||
          isDraggingTasks) && (
          <div
            style={{ marginBottom: '16px', backgroundColor: background }}
            ref={dropRef}
          >
            <StatusCard status={status} color={color} />
            {statusTask.map((tsk) => (
              <TaskCard key={tsk.id} color={color} task={tsk} />
            ))}
            {isDraggingTasks && <div style={{ height: '32px' }} />}
          </div>
        )}
    </>
  );
};

export default StatusColumn;
